import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { map, StorageUtil } from '../store';
import * as lodash from 'lodash';
import * as moment from 'moment';
import { max } from 'rxjs/operators';

@Injectable()
export class WorkoutService {

  url = '/api/workout';

  constructor(
    private hs: HttpService
  ) { }

  getWorkoutPlans(teamID: number) {
    return this.hs.get(`${this.url}/get-by-team/${teamID}`);
  }

  cloneWorkoutPlan(planID: number) {
    return this.hs.post(`${this.url}/clone-workout-plan/${planID}`, StorageUtil.userID);
  }

  updateWorkoutPlan(plan) {
    return this.hs.post(`${this.url}/update-plan`, plan);
  }

  archiveWorkoutPlans(plans) {
    return this.hs.post(`${this.url}/archive-plans`, plans);
  }

  getWorkoutTypes() {
    return this.hs.get(`${this.url}/get-workout-types`);
  }

  getWorkoutCategories() {
    return this.hs.get(`${this.url}/get-workout-categories`);
  }

  getWorkoutIntervalOptions() {
    return this.hs.get(`${this.url}/get-workout-interval-options`);
  }

  getWorkoutList(planID: number) {
    return this.hs.get(`${this.url}/get-workouts-list/${planID}`).pipe(
      map(res => lodash.sortBy(res, ['date', 'name']).reverse())
    );
  }

  getWorkoutRecord(planID: number) {
    return this.hs.get(`${this.url}/get-workouts-records/${planID}`).pipe(
      map(res => lodash.sortBy(res, ['date', 'name']).reverse())
    );
  }

  archiveWorkouts(wokrouts) {
    return this.hs.post(`${this.url}/delete-workouts`, wokrouts);
  }

  updateWorkout(wokrout) {
    return this.hs.post(`${this.url}/update-workout`, { ...wokrout, clubID: StorageUtil.clubID, teamID: StorageUtil.teamId });
  }

  updateWorkoutOrder(workouts) {
    return this.hs.post(`${this.url}/update-workouts-order`, workouts);
  }

  assignPlayers(players: number[], planID: number) {
    return this.hs.post(`${this.url}/assign-players`, { players, planID, clubID: StorageUtil.clubID, teamID: StorageUtil.teamId });
  }

  getWorkoutLeaderBoard(catID: number, clubID: number, type: string, teamID: number, playerID:number) {
    return this.hs.get(`${this.url}/get-workout-leaderboard/${catID}/${clubID}/${type}/${teamID}/${playerID}`);
  }

  getWorkoutPlayersResult(planID: number, workoutID: number) {
    return this.hs.get(`${this.url}/get-workout-players-result/${planID}/${workoutID}`);
  }

  getTeamWorkoutSettings(teamID: number) {
    return this.hs.get(`${this.url}/get-team-workout-settings/${teamID}`);
  }

  getTeamWorkoutPlayers(teamID: number, startDate, endDate, userID = 0) {
    return this.hs.get(`${this.url}/get-team-workout-players/${teamID}`, { params: { startDate, endDate, userID } });
  }

  updateTeamWorkoutSettings(data) {
    return this.hs.post(`${this.url}/update-team-workout-settings`, data);
  }

  getWorkoutPlayers(planID: number, workoutID: number, userID: number = 0) {
    return this.hs.get(`${this.url}/get-workout-players/${planID}/${workoutID}`, { params: { userID } }).pipe(map(players => {
      players.forEach(p => {
        try {
          p.pastDueDays = moment(new Date()).diff(moment(p.dateDue), 'day');
          try {
            p.workoutSummary = p.workoutSummary ? JSON.parse(p.workoutSummary) : [];
          } catch {
            p.workoutSummary = [];
          }
          let milestones = [];
          const summaries = [];
          let index = 0;
          for (const s of p.workoutSummary) {
            if (s.Dst - index > 1) {
              index++;
              if (milestones.length) {
                summaries.push(this.getSummary(milestones, index));
              }
              milestones = [];
            }
            milestones.push(s);
          }
          if (milestones.length) {
            summaries.push(this.getSummary(milestones, p.distance));
          }
          p.summaries = summaries;

          const leaderboardSummaries = [];
          let leaderboardIndex = 0;
          let leaderboardMilestones = [];
          for (const s of p.workoutSummary) {
            if (s.Dst - leaderboardIndex > 0.5) {
              leaderboardIndex += 0.5;
              if (leaderboardMilestones.length) {
                leaderboardSummaries.push(this.getSummary(leaderboardMilestones, leaderboardIndex));
              }
              leaderboardMilestones = [];
            }
            leaderboardMilestones.push(s);
          }
          if (leaderboardMilestones.length) {
            leaderboardSummaries.push(this.getSummary(leaderboardMilestones, p.distance));
          }
          p.summaries = summaries;
          p.leaderboardSummaries = leaderboardSummaries;
        } catch (e) {
          console.log('----------', e);
          p.workoutSummary = [];
          p.summaries = [];
        }
      });
      return players;
    }));
  }

  getTechnicalWorkoutData(playerID, selectedYear, selectedMonth) {
    return this.hs.get(`/api/Workout/get-player-technical-workouts-list/${playerID}/${selectedYear}/${selectedMonth}`);
  }

  getTeamWorkoutLastDate(teamID: number, userID = 0) {
    return this.hs.get(`${this.url}/get-team-workout-last/${teamID}`, { params: { userID } });
  }

  getPlayerCompletedWorkoutDetailsForWeb(workoutplayerID: number) {
    return this.hs.get(`${this.url}/get-player-completed-workout-details-for-web/${workoutplayerID}`);
  }

  private getSummary(milestones, miles: number) {
    const first = milestones[0];
    const last = milestones[milestones.length - 1];
    const time = last.Ts - first.Ts;
    const minutes = Math.floor(time / 60).toString();
    const seconds = (time % 60).toString();
    const avg = (last.Ts - first.Ts) / (last.Dst - first.Dst) / 60;
    const min = Math.floor(avg);
    const sec = Math.floor((avg - min) * 60);

    const totalMinutes = Math.floor(last.Ts / 60).toString();
    const totalSeconds = (last.Ts % 60).toString();
    return {
      mile: miles.toFixed(2),
      time: (minutes.length === 1 ? '0' + minutes : minutes) + ':' + (seconds.length === 1 ? '0' + seconds : seconds),
      totalTime: (totalMinutes.length === 1 ? '0' + totalMinutes : totalMinutes) + ':' + (totalSeconds.length === 1 ? '0' + totalSeconds : totalSeconds),
      maxElav: lodash.sumBy(milestones.filter(m => m.El > 0), 'El').toFixed(0), // Math.max(...milestones.map(m => m.El)),
      minElav: lodash.sumBy(milestones.filter(m => m.El < 0), 'El').toFixed(0), // Math.min(...milestones.map(m => m.El)),
      avgPace: min + ':' + (sec < 10 ? '0' + sec : sec),
      date: new Date(first.Date)
    };
  }

}



// WEBPACK FOOTER //
// ./src/app/services/workout.service.ts